import React, { useState, useEffect } from 'react';
import {
  Grid,
  List,
  Search,
  Filter,
  Star,
  Copy,
  Trash2,
  Edit2,
  Eye,
  EyeOff,
  Cloud,
  Download,
  Share2,
  Calendar,
  Tag,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Loader,
  X,
  Save,
  RefreshCw,
  BookOpen,
  Clock,
  GitBranch,
  FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { promptsService, templatesService } from '../services/firebase/firestore';
import { PromptDocument, TemplateDocument } from '../services/firebase/firestore';
import * as storiesService from '../services/firebase/storiesService';
import { SavedStory } from '../types/story';
import { exportStorage } from '../services/firebase/storage';
import toast from 'react-hot-toast';

const UserDashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'prompts' | 'templates' | 'stories' | 'shared'>('prompts');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'favorite' | 'alphabetical'>('recent');
  
  const [prompts, setPrompts] = useState<PromptDocument[]>([]);
  const [templates, setTemplates] = useState<TemplateDocument[]>([]);
  const [stories, setStories] = useState<SavedStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDocument | null>(null);
  const [selectedStory, setSelectedStory] = useState<SavedStory | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<PromptDocument | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, activeTab]);

  const loadData = async () => {
    if (!user) return;
    
    console.log('Loading dashboard data for user:', user.uid);
    setLoading(true);
    try {
      const [promptsData, templatesData, storiesData] = await Promise.all([
        promptsService.getUserPrompts(user.uid, 50),
        templatesService.getUserTemplates(user.uid),
        storiesService.getUserStories(user.uid)
      ]);
      
      console.log('Loaded prompts:', promptsData.data);
      console.log('Loaded templates:', templatesData.data);
      console.log('Loaded stories:', storiesData);
      
      setPrompts(promptsData.data);
      setTemplates(templatesData.data);
      setStories(storiesData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (promptId: string, currentStatus: boolean) => {
    try {
      await promptsService.toggleFavorite(promptId, !currentStatus);
      setPrompts(prompts.map(p => 
        p.id === promptId ? { ...p, isFavorite: !currentStatus } : p
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await promptsService.delete(promptId);
      setPrompts(prompts.filter(p => p.id !== promptId));
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };

  const handleTogglePublic = async (promptId: string, currentStatus: boolean) => {
    try {
      await promptsService.update(promptId, { isPublic: !currentStatus });
      setPrompts(prompts.map(p => 
        p.id === promptId ? { ...p, isPublic: !currentStatus } : p
      ));
    } catch (error) {
      console.error('Error toggling public status:', error);
    }
  };

  const handleExportPrompt = async (prompt: PromptDocument) => {
    try {
      const exportData = {
        title: prompt.title,
        content: prompt.content,
        platform: prompt.platform,
        category: prompt.category,
        tags: prompt.tags,
        parameters: prompt.parameters,
        createdAt: prompt.createdAt,
        exportedAt: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${prompt.title.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting prompt:', error);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingPrompt || !editingPrompt.id) return;
    
    try {
      await promptsService.update(editingPrompt.id, {
        title: editingPrompt.title,
        content: editingPrompt.content,
        platform: editingPrompt.platform,
        category: editingPrompt.category,
        tags: editingPrompt.tags
      });
      
      setPrompts(prompts.map(p => 
        p.id === editingPrompt.id ? editingPrompt : p
      ));
      setEditingPrompt(null);
    } catch (error) {
      console.error('Error saving prompt:', error);
    }
  };

  const copyToClipboard = (text: string, itemId?: string) => {
    navigator.clipboard.writeText(text);
    toast.success('📋 Copied to clipboard!');
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  // Filter and sort prompts
  const filteredPrompts = prompts
    .filter(p => {
      const matchesSearch = searchQuery === '' || 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPlatform = filterPlatform === 'all' || p.platform === filterPlatform;
      return matchesSearch && matchesPlatform;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else if (sortBy === 'favorite') {
        return (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0);
      } else {
        return a.title.localeCompare(b.title);
      }
    });

  if (!isAuthenticated) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center py-16">
          <Cloud className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view your dashboard</h2>
          <p className="text-gray-600">Access your saved prompts, templates, and more</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">My Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-300">Manage your prompts, templates, and shared content</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-slate-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'prompts'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-600 border-transparent hover:text-gray-800'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            My Prompts ({prompts.length})
          </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'text-cyan-600 dark:text-cyan-400 border-cyan-500'
              : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          <Grid className="w-4 h-4 inline mr-2" />
          Templates ({templates.length})
        </button>
        <button
          onClick={() => setActiveTab('stories')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'stories'
              ? 'text-cyan-600 dark:text-cyan-400 border-cyan-500'
              : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          <BookOpen className="w-4 h-4 inline mr-2" />
          Stories ({stories.length})
        </button>
        <button
          onClick={() => setActiveTab('shared')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'shared'
              ? 'text-cyan-600 dark:text-cyan-400 border-cyan-500'
              : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          <Share2 className="w-4 h-4 inline mr-2" />
          Shared with Me
        </button>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="px-3 py-1 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center gap-1 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search prompts..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
            />
          </div>
        </div>

        {/* Filters */}
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
        >
          <option value="all">All Platforms</option>
          <option value="midjourney">Midjourney</option>
          <option value="dalle3">DALL-E 3</option>
          <option value="stable-diffusion">Stable Diffusion</option>
          <option value="veo">Google Veo</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
        >
          <option value="recent">Most Recent</option>
          <option value="favorite">Favorites First</option>
          <option value="alphabetical">Alphabetical</option>
        </select>

        {/* View Mode */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {activeTab === 'prompts' && (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
              {filteredPrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow ${
                    viewMode === 'list' ? 'p-4' : 'p-5'
                  }`}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {prompt.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {new Date(prompt.createdAt).toLocaleDateString()}
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded">
                          {prompt.platform}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleFavorite(prompt.id!, prompt.isFavorite)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        prompt.isFavorite 
                          ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' 
                          : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Star className={`w-4 h-4 ${prompt.isFavorite ? 'fill-current' : ''}`} />
                    </button>
                  </div>

                  {/* Content Preview */}
                  <div className="mb-3">
                    <p className={`text-sm text-gray-600 dark:text-gray-400 ${
                      expandedItems.has(prompt.id!) ? '' : 'line-clamp-3'
                    }`}>
                      {prompt.content}
                    </p>
                    {prompt.content.length > 150 && (
                      <button
                        onClick={() => toggleExpanded(prompt.id!)}
                        className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 mt-1 flex items-center gap-1 transition-colors"
                      >
                        {expandedItems.has(prompt.id!) ? (
                          <>Show less <ChevronUp className="w-3 h-3" /></>
                        ) : (
                          <>Show more <ChevronDown className="w-3 h-3" /></>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Tags */}
                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {prompt.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 text-xs rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t dark:border-slate-700">
                    <button
                      onClick={() => copyToClipboard(prompt.content)}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title="Copy prompt"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTogglePublic(prompt.id!, prompt.isPublic)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        prompt.isPublic 
                          ? 'text-green-600 bg-green-50 dark:bg-green-900/20' 
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title={prompt.isPublic ? 'Make private' : 'Make public'}
                    >
                      {prompt.isPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleExportPrompt(prompt)}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title="Export prompt"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingPrompt(prompt)}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title="Edit prompt"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePrompt(prompt.id!)}
                      className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-auto"
                      title="Delete prompt"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {template.uses || 0} uses
                    </span>
                    <div className="flex gap-2">
                      <button className="text-blue-600 hover:text-blue-700 text-sm">
                        Use Template
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'stories' && (
            <div className="space-y-4">
              {stories.length === 0 ? (
                <div className="text-center py-16">
                  <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No stories yet</h3>
                  <p className="text-gray-600 dark:text-gray-400">Your viral stories will appear here</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {stories.map((story) => (
                    <div
                      key={story.id}
                      className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">
                            {story.topic}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {story.ideaTitle}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {story.ideaDescription}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {story.isFavorite && (
                            <Star className="w-5 h-5 text-yellow-500 fill-current" />
                          )}
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs rounded-full flex items-center gap-1">
                            <GitBranch className="w-3 h-3" />
                            {story.versions.length} versions
                          </span>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                          {story.currentVersion.fullStory}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(story.createdAt).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(story.updatedAt).toLocaleTimeString()}
                          </span>
                          {story.viewCount && story.viewCount > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {story.viewCount} views
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedStory(story);
                            storiesService.incrementViewCount(story.id);
                          }}
                          className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Story
                        </button>
                        <button
                          onClick={() => copyToClipboard(story.currentVersion.fullStory, story.id)}
                          className="px-3 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            await storiesService.toggleStoryFavorite(story.id);
                            loadData();
                          }}
                          className="px-3 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                        >
                          <Star className={`w-4 h-4 ${story.isFavorite ? 'fill-current text-yellow-500' : ''}`} />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this story?')) {
                              await storiesService.deleteStory(story.id);
                              toast.success('Story deleted');
                              loadData();
                            }
                          }}
                          className="px-3 py-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'shared' && (
            <div className="text-center py-16">
              <Share2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No shared content yet</h3>
              <p className="text-gray-600">Prompts shared with you will appear here</p>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && filteredPrompts.length === 0 && activeTab === 'prompts' && (
        <div className="text-center py-16">
          <Sparkles className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No prompts found</h3>
          <p className="text-gray-600">
            {searchQuery ? 'Try adjusting your search or filters' : 'Start creating prompts to see them here'}
          </p>
        </div>
      )}

      {/* Edit Modal */}
      {editingPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b dark:border-slate-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Prompt</h2>
              <button onClick={() => setEditingPrompt(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editingPrompt.title}
                  onChange={(e) => setEditingPrompt({...editingPrompt, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prompt Content
                </label>
                <textarea
                  value={editingPrompt.content}
                  onChange={(e) => setEditingPrompt({...editingPrompt, content: e.target.value})}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Platform */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Platform
                </label>
                <select
                  value={editingPrompt.platform}
                  onChange={(e) => setEditingPrompt({...editingPrompt, platform: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="midjourney">Midjourney</option>
                  <option value="dalle">DALL-E</option>
                  <option value="stable-diffusion">Stable Diffusion</option>
                  <option value="google-veo">Google Veo</option>
                  <option value="runway">Runway ML</option>
                  <option value="pika">Pika Labs</option>
                  <option value="universal">Universal</option>
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={editingPrompt.category || ''}
                  onChange={(e) => setEditingPrompt({...editingPrompt, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Category</option>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                  <option value="abstract">Abstract</option>
                  <option value="realistic">Realistic</option>
                  <option value="anime">Anime</option>
                  <option value="3d">3D</option>
                  <option value="illustration">Illustration</option>
                  <option value="photography">Photography</option>
                  <option value="concept-art">Concept Art</option>
                  <option value="architecture">Architecture</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={editingPrompt.tags?.join(', ') || ''}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt, 
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                  })}
                  placeholder="e.g., cinematic, detailed, vibrant"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t dark:border-gray-700">
              <button
                onClick={() => setEditingPrompt(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Viewer Modal with Version History */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b dark:border-slate-700">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {selectedStory.topic}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedStory.ideaTitle}
                </p>
              </div>
              <button onClick={() => setSelectedStory(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex h-[75vh]">
              {/* Story Content */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-6">
                  {/* Full Story */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-6 border-2 border-purple-200 dark:border-purple-700">
                    <h3 className="font-bold text-lg mb-3 text-gray-900 dark:text-white">Full Story</h3>
                    <p className="whitespace-pre-wrap text-gray-800 dark:text-gray-300 leading-relaxed">
                      {selectedStory.currentVersion.fullStory}
                    </p>
                  </div>
                  
                  {/* Story Parts */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">Story Structure</h3>
                    {Object.entries(selectedStory.currentVersion.parts).map(([key, value]) => (
                      <div key={key} className="bg-white dark:bg-slate-700 rounded-lg p-4 border border-gray-200 dark:border-slate-600">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                        <p className="text-gray-700 dark:text-gray-300">{value}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Metadata */}
                  <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3 text-gray-900 dark:text-white">Metadata</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Hook Type:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedStory.currentVersion.metadata.hookType}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Emotional Tone:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedStory.currentVersion.metadata.emotionalTone}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Target Audience:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedStory.currentVersion.metadata.targetAudience}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Read Time:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedStory.currentVersion.metadata.estimatedReadTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Version History Sidebar */}
              <div className="w-80 border-l dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-4 overflow-y-auto">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                  <GitBranch className="w-5 h-5" />
                  Version History ({selectedStory.versions.length})
                </h3>
                
                <div className="space-y-3">
                  {selectedStory.versions.slice().reverse().map((version, idx) => (
                    <div
                      key={version.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        version.id === selectedStory.currentVersion.id
                          ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600'
                          : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                      onClick={async () => {
                        if (version.id !== selectedStory.currentVersion.id) {
                          await storiesService.restoreStoryVersion(selectedStory.id, version.id);
                          toast.success('Version restored!');
                          loadData();
                          setSelectedStory(null);
                        }
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">
                          Version {version.versionNumber}
                        </span>
                        {version.id === selectedStory.currentVersion.id && (
                          <span className="text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                        {new Date(version.createdAt).toLocaleString()}
                      </p>
                      {version.changeDescription && (
                        <p className="text-xs text-gray-700 dark:text-gray-300 italic">
                          {version.changeDescription}
                        </p>
                      )}
                      {version.id !== selectedStory.currentVersion.id && (
                        <button className="text-xs text-purple-600 dark:text-purple-400 hover:underline mt-2">
                          Restore this version
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="border-t dark:border-slate-700 p-4 flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard(selectedStory.currentVersion.fullStory)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Story
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([selectedStory.currentVersion.fullStory], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedStory.topic.replace(/\s+/g, '-')}-story.txt`;
                    a.click();
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
              <button
                onClick={() => setSelectedStory(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;